import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode, RefObject } from 'react';

interface ProfileRailDock {
    /**
     * True once the tab band is CLOSE to the top — see `DOCK_LEAD_PX`. The
     * badge's own animation runs off this, and starts before the band lands.
     */
    docked: boolean;
    setDocked: (docked: boolean) => void;
    /**
     * True once the band has actually locked to the top. Distinct from
     * `docked` because the band's own background may only appear at the lock
     * point — arriving on the lead would show it while the band is still
     * travelling, over a photograph it is not yet sitting against.
     */
    stuck: boolean;
    setStuck: (stuck: boolean) => void;
}

const ProfileRailDockContext = createContext<ProfileRailDock>({
    docked: false,
    setDocked: () => undefined,
    stuck: false,
    setStuck: () => undefined,
});

/**
 * Shares one piece of state between the brand badge and the tab band: whether
 * the band has reached the top of the viewport.
 *
 * They are siblings, not parent and child — the badge has to travel the whole
 * page, so it cannot live inside the band — and the badge's docked size is a
 * fact about the band's position. Hence a context rather than a prop.
 */
export const ProfileRailDockProvider = ({
    children,
}: {
    children: ReactNode;
}) => {
    const [docked, setDocked] = useState(false);
    const [stuck, setStuck] = useState(false);
    const value = useMemo(
        () => ({ docked, setDocked, stuck, setStuck }),
        [docked, stuck],
    );
    return (
        <ProfileRailDockContext.Provider value={value}>
            {children}
        </ProfileRailDockContext.Provider>
    );
};

/** Read the docked state (the badge). */
export const useProfileRailDocked = () =>
    useContext(ProfileRailDockContext).docked;

/** Read the locked state (the band's own background). */
export const useProfileRailStuck = () =>
    useContext(ProfileRailDockContext).stuck;

/**
 * How far above the lock point the group starts forming. The badge takes 300ms
 * to reach its docked size, so waiting for the band to actually land reads as
 * lag — it begins closing while the band is still on its way in.
 */
const DOCK_LEAD_PX = 60;

/**
 * Report the docked state (the tab band). Give it the band's own ref: the band
 * is `sticky top-0`, so its top edge measures the distance left to the lock.
 */
export const useReportProfileRailDock = (
    bandRef: RefObject<HTMLElement | null>,
) => {
    const { setDocked, setStuck } = useContext(ProfileRailDockContext);

    useEffect(() => {
        const band = bandRef.current;
        if (!band) return;

        // One layout read, two answers: the lead the badge animates on and the
        // lock the band's background waits for.
        //
        // Read straight out of the scroll handler rather than deferred to a
        // frame. It is a single getBoundingClientRect on an element already in
        // the layout tree, which is cheaper than the bookkeeping around
        // deferring it — and a frame callback does not run at all while the
        // tab is hidden, which left the band with no ground when the reader
        // came back to a page already scrolled past the lock.
        const measure = () => {
            const { top } = band.getBoundingClientRect();
            setDocked(top <= DOCK_LEAD_PX);
            setStuck(top <= 0);
        };

        measure();
        window.addEventListener('scroll', measure, { passive: true });
        window.addEventListener('resize', measure, { passive: true });
        return () => {
            window.removeEventListener('scroll', measure);
            window.removeEventListener('resize', measure);
        };
    }, [bandRef, setDocked, setStuck]);
};
